// ── Fish Audio S2-Pro emotion tag vocabulary ─────────────────────────────
//
// Source: https://docs.fish.audio/api-reference/emotion-reference
//
// s2-pro accepts inline natural-language tags in [bracket] syntax. The list
// below is the canonical 64+ vocabulary documented by Fish Audio. The model
// also accepts freeform descriptions ([dead tired voice], [professional
// broadcast tone]) — this list is a discovery aid for the autocomplete, not
// a hard validator.
//
// Placement rule: tags work best at sentence/phrase boundaries, not mid-word.
// The autocomplete does NOT enforce position; the helper text in Generate.tsx
// sets that expectation.

export type EmotionTagCategory =
  | "basic"      // Basic emotions
  | "advanced"   // Advanced emotions
  | "tone"       // Tone markers (volume / hurry / whisper)
  | "audio"      // Audio effects (laugh, sigh, cry, etc)
  | "special";   // Special effects (audience, breaks)

export interface EmotionTag {
  /** Canonical bracket-content, lowercase, no surrounding `[]`. */
  tag: string;
  category: EmotionTagCategory;
  /** Display label (Title Case) for the popover. */
  label: string;
}

export const EMOTION_TAGS: EmotionTag[] = [
  // Basic emotions (24)
  { tag: "happy", category: "basic", label: "Happy" },
  { tag: "sad", category: "basic", label: "Sad" },
  { tag: "angry", category: "basic", label: "Angry" },
  { tag: "excited", category: "basic", label: "Excited" },
  { tag: "calm", category: "basic", label: "Calm" },
  { tag: "nervous", category: "basic", label: "Nervous" },
  { tag: "confident", category: "basic", label: "Confident" },
  { tag: "surprised", category: "basic", label: "Surprised" },
  { tag: "satisfied", category: "basic", label: "Satisfied" },
  { tag: "delighted", category: "basic", label: "Delighted" },
  { tag: "scared", category: "basic", label: "Scared" },
  { tag: "worried", category: "basic", label: "Worried" },
  { tag: "upset", category: "basic", label: "Upset" },
  { tag: "frustrated", category: "basic", label: "Frustrated" },
  { tag: "depressed", category: "basic", label: "Depressed" },
  { tag: "empathetic", category: "basic", label: "Empathetic" },
  { tag: "embarrassed", category: "basic", label: "Embarrassed" },
  { tag: "disgusted", category: "basic", label: "Disgusted" },
  { tag: "moved", category: "basic", label: "Moved" },
  { tag: "proud", category: "basic", label: "Proud" },
  { tag: "relaxed", category: "basic", label: "Relaxed" },
  { tag: "grateful", category: "basic", label: "Grateful" },
  { tag: "curious", category: "basic", label: "Curious" },
  { tag: "sarcastic", category: "basic", label: "Sarcastic" },

  // Advanced emotions (25)
  { tag: "disdainful", category: "advanced", label: "Disdainful" },
  { tag: "unhappy", category: "advanced", label: "Unhappy" },
  { tag: "anxious", category: "advanced", label: "Anxious" },
  { tag: "hysterical", category: "advanced", label: "Hysterical" },
  { tag: "indifferent", category: "advanced", label: "Indifferent" },
  { tag: "uncertain", category: "advanced", label: "Uncertain" },
  { tag: "doubtful", category: "advanced", label: "Doubtful" },
  { tag: "confused", category: "advanced", label: "Confused" },
  { tag: "disappointed", category: "advanced", label: "Disappointed" },
  { tag: "regretful", category: "advanced", label: "Regretful" },
  { tag: "guilty", category: "advanced", label: "Guilty" },
  { tag: "ashamed", category: "advanced", label: "Ashamed" },
  { tag: "jealous", category: "advanced", label: "Jealous" },
  { tag: "envious", category: "advanced", label: "Envious" },
  { tag: "hopeful", category: "advanced", label: "Hopeful" },
  { tag: "optimistic", category: "advanced", label: "Optimistic" },
  { tag: "pessimistic", category: "advanced", label: "Pessimistic" },
  { tag: "nostalgic", category: "advanced", label: "Nostalgic" },
  { tag: "lonely", category: "advanced", label: "Lonely" },
  { tag: "bored", category: "advanced", label: "Bored" },
  { tag: "contemptuous", category: "advanced", label: "Contemptuous" },
  { tag: "sympathetic", category: "advanced", label: "Sympathetic" },
  { tag: "compassionate", category: "advanced", label: "Compassionate" },
  { tag: "determined", category: "advanced", label: "Determined" },
  { tag: "resigned", category: "advanced", label: "Resigned" },

  // Tone markers (5)
  { tag: "in a hurry tone", category: "tone", label: "In a hurry" },
  { tag: "shouting", category: "tone", label: "Shouting" },
  { tag: "screaming", category: "tone", label: "Screaming" },
  { tag: "whispering", category: "tone", label: "Whispering" },
  { tag: "soft tone", category: "tone", label: "Soft tone" },

  // Audio effects (10)
  { tag: "laughing", category: "audio", label: "Laughing" },
  { tag: "chuckling", category: "audio", label: "Chuckling" },
  { tag: "sobbing", category: "audio", label: "Sobbing" },
  { tag: "crying loudly", category: "audio", label: "Crying loudly" },
  { tag: "sighing", category: "audio", label: "Sighing" },
  { tag: "groaning", category: "audio", label: "Groaning" },
  { tag: "panting", category: "audio", label: "Panting" },
  { tag: "gasping", category: "audio", label: "Gasping" },
  { tag: "yawning", category: "audio", label: "Yawning" },
  { tag: "snoring", category: "audio", label: "Snoring" },

  // Special effects (5)
  { tag: "audience laughing", category: "special", label: "Audience laughing" },
  { tag: "background laughter", category: "special", label: "Background laughter" },
  { tag: "crowd laughing", category: "special", label: "Crowd laughing" },
  { tag: "break", category: "special", label: "Break (short pause)" },
  { tag: "long-break", category: "special", label: "Long break" },
];

export const CATEGORY_LABELS: Record<EmotionTagCategory, string> = {
  basic: "Emotions",
  advanced: "More emotions",
  tone: "Tone",
  audio: "Audio effect",
  special: "Special",
};

/**
 * Detect an open emotion-tag bracket at or before `caret` in `text`.
 *
 * Walks backwards from the caret looking for `[`. Bails on `]` or newline
 * (which would close any open tag) and on `\[` (escape).
 *
 * Returns { openPos, partial } if there's an unclosed `[` since the most
 * recent boundary, with `openPos` pointing AT the `[` and `partial` being
 * the chars between `[` and the caret. Returns null otherwise.
 */
export function detectOpenTag(
  text: string,
  caret: number,
): { openPos: number; partial: string } | null {
  for (let i = caret - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === "]" || ch === "\n") return null;
    if (ch === "[") {
      // Don't trigger if escaped: `\[`
      if (i > 0 && text[i - 1] === "\\") return null;
      return { openPos: i, partial: text.slice(i + 1, caret) };
    }
  }
  return null;
}

/**
 * Score a tag against a partial query. Returns null if no match.
 * Lower score = better match. Used for sort order.
 *
 *   exact prefix (case-insensitive): 0
 *   word-boundary prefix:            10
 *   substring contained:             20
 *   subsequence (fuzzy):             30
 *   no match:                        null
 */
export function scoreTag(tag: EmotionTag, partial: string): number | null {
  if (!partial) return 0; // empty query → all tags, original order
  const q = partial.toLowerCase();
  const t = tag.tag.toLowerCase();

  if (t.startsWith(q)) return 0;
  if (t.split(" ").some((word) => word.startsWith(q))) return 10;
  if (t.includes(q)) return 20;

  // Subsequence fuzzy match: every char of q appears in t in order
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length ? 30 : null;
}

/** Filter and sort the emotion tag vocabulary by a partial query. */
export function filterTags(partial: string): EmotionTag[] {
  const scored = EMOTION_TAGS
    .map((t) => ({ tag: t, score: scoreTag(t, partial) }))
    .filter((x): x is { tag: EmotionTag; score: number } => x.score !== null)
    .sort((a, b) => a.score - b.score);
  return scored.map((s) => s.tag);
}
